import React from 'react';
import ReactDOM from 'react-dom';

interface PortalWrapperProps {
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	enabled?: boolean;
	targetRef?: React.RefObject<HTMLElement>;
}

interface PortalWrapperState {
	portalStyle: React.CSSProperties;
}

export class PortalWrapper extends React.Component<PortalWrapperProps, PortalWrapperState> {
	private placeholderRef: React.RefObject<HTMLDivElement>;
	private portalContainer: HTMLDivElement | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private componentMounted = false;

	constructor(props: PortalWrapperProps) {
		super(props);
		this.placeholderRef = React.createRef();
		this.state = {
			portalStyle: {},
		};
	}

	private getTargetElement(): HTMLElement | null {
		if (this.props.targetRef?.current) {
			return this.props.targetRef.current;
		}
		return this.placeholderRef.current;
	}

	private updatePosition = (): void => {
		if (!this.props.enabled || !this.portalContainer) return;

		const targetElement = this.getTargetElement();
		if (!targetElement) return;

		const rect = targetElement.getBoundingClientRect();

		this.setState({
			portalStyle: {
				position: 'fixed',
				top: `${rect.top}px`,
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				minWidth: `${rect.width}px`,
				pointerEvents: 'auto',
			},
		});
	};

	private setupObservers(): void {
		if (!this.props.enabled) return;

		const targetElement = this.getTargetElement();
		if (!targetElement) return;

		if (typeof ResizeObserver !== 'undefined') {
			this.resizeObserver = new ResizeObserver(this.updatePosition);
			this.resizeObserver.observe(targetElement);
		}

		window.addEventListener('scroll', this.updatePosition);
		window.addEventListener('resize', this.updatePosition);
	}

	private cleanupObservers(): void {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}

		window.removeEventListener('scroll', this.updatePosition);
		window.removeEventListener('resize', this.updatePosition);
	}

	private createPortalContainer(): void {
		if (this.portalContainer) return;

		const container = document.createElement('div');
		container.className = 'portal-wrapper-container';
		
		const style = container.style;
		style.position = 'fixed';
		style.top = '0';
		style.left = '0';
		style.width = '0';
		style.height = '0';
		style.zIndex = '1000';
		style.pointerEvents = 'none';
		
		document.body.appendChild(container);
		this.portalContainer = container;
	}

	private removePortalContainer(): void {
		if (!this.portalContainer) return;

		this.cleanupObservers();
		
		if (this.portalContainer.parentNode) {
			this.portalContainer.parentNode.removeChild(this.portalContainer);
		}
		this.portalContainer = null;
	}

	componentDidMount(): void {
		this.componentMounted = true;
		
		if (this.props.enabled) {
			this.createPortalContainer();
			// Ждем следующего тика для вычисления позиции
			setTimeout(() => {
				if (this.componentMounted) {
					this.updatePosition();
					this.setupObservers();
				}
			}, 0);
		}
	}

	componentDidUpdate(prevProps: PortalWrapperProps): void {
		// Если enabled изменился
		if (prevProps.enabled !== this.props.enabled) {
			if (this.props.enabled) {
				this.createPortalContainer();
				setTimeout(() => {
					if (this.componentMounted) {
						this.updatePosition();
						this.setupObservers();
					}
				}, 0);
			} else {
				this.removePortalContainer();
				this.setState({ portalStyle: {} });
			}
		}
		// Если targetRef изменился
		else if (prevProps.targetRef !== this.props.targetRef && this.props.enabled) {
			this.cleanupObservers();
			setTimeout(() => {
				if (this.componentMounted) {
					this.updatePosition();
					this.setupObservers();
				}
			}, 0);
		}
	}

	componentWillUnmount(): void {
		this.componentMounted = false;
		this.removePortalContainer();
	}

	render(): React.ReactNode {
		const { 
			children, 
			className = '', 
			style = {}, 
			enabled = true,
			targetRef 
		} = this.props;
		
		const { portalStyle } = this.state;

		if (!enabled) {
			return (
				<div className={className + " portal-body"} style={style}>
					{children}
				</div>
			);
		}

		// Рендерим placeholder только если НЕ передан targetRef
		const shouldRenderPlaceholder = !targetRef;
		
		// Рендерим контент в портал если есть контейнер
		const portalContent = this.portalContainer ? (
			ReactDOM.createPortal(
				<div
					className={className + " portal-content"}
					style={{
						...style,
						...portalStyle,
					}}
				>
					{children}
				</div>,
				this.portalContainer
			)
		) : null;

		return (
			<>
				{shouldRenderPlaceholder && (
					<div
						ref={this.placeholderRef}
						className={className + " portal-placeholder"}
						style={{
							...style,
							visibility: 'hidden',
							pointerEvents: 'none',
							position: 'relative',
						}}
						aria-hidden="true"
					/>
				)}

				{portalContent}
			</>
		);
	}
}